import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, FileText } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  salaryCycleCreateSchema,
  type SalaryCycleInput,
} from '@bbl/validation';
import { apiRequest, ApiError, type ListResponse, openApiDocument } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Drawer } from '../components/ui/Drawer';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { TextField, SelectField, TextAreaField } from '../components/ui/Field';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { Pagination } from '../components/people/Pagination';
import { SalaryStatusBadge } from '../components/finance/StatusBadge';
import { CycleSidebar, CycleSelect } from '../components/finance/CycleSidebar';
import { SalaryMarkPaidDrawer } from '../components/finance/SalaryMarkPaidDrawer';
import { normaliseSalaryRow } from '../components/finance/normalisers';
import { formatINR } from '../lib/format';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  salaryPaymentsFilterConfig,
  salaryPaymentsSearchSchema,
  SALARY_PAYMENTS_SORT_COLUMNS,
} from '../components/finance/salaryPaymentsFilterConfig';
import {
  cycleLabel,
  type SalaryCycle,
  type SalaryPaymentJoined,
} from '../components/finance/types';

const PAGE_SIZE = 20;

/** A loaded page of the per-cycle payouts grid: normalised rows + the total. */
interface SalaryPaymentsPage {
  items: SalaryPaymentJoined[];
  total: number;
}

export const Route = createFileRoute('/_authed/finance/salaries')({
  validateSearch: salaryPaymentsSearchSchema,
  component: SalariesPage,
});

function SalariesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canManage = role === 'super_admin' || role === 'finance_admin';
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [newCycleOpen, setNewCycleOpen] = useState(false);
  const [markPaidPayment, setMarkPaidPayment] = useState<SalaryPaymentJoined | null>(null);

  // The single source of truth for filters / sort / page WITHIN the selected
  // cycle, backed by the route's URL search params. The selected cycle itself
  // stays local state (the cycle picker) — it is NOT a toolkit facet. The
  // adapter narrows the route's precisely-typed search/navigate surface to the
  // toolkit's structural `FilterRoute` contract; the route's own Zod
  // `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(salaryPaymentsFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;

  const cyclesQuery = useQuery({
    queryKey: ['salaryCycles'] as const,
    queryFn: async ({ signal }) => {
      const res = await apiRequest<{ items: SalaryCycle[] }>('/api/salaries/cycles', {
        signal,
      });
      return res.items;
    },
  });

  useEffect(() => {
    if (selectedCycleId) return;
    const first = cyclesQuery.data?.[0];
    if (first) setSelectedCycleId(first.id);
  }, [cyclesQuery.data, selectedCycleId]);

  const paymentsQuery = useQuery<SalaryPaymentsPage>({
    // The cycle id is the selector, merged with the toolkit's emitted facet /
    // sort / page params. Both belong in the key so a change to either refetches.
    queryKey: ['salaryPayments', selectedCycleId, apiParams] as const,
    enabled: !!selectedCycleId,
    placeholderData: (prev) => prev,
    queryFn: async ({ signal }): Promise<SalaryPaymentsPage> => {
      if (!selectedCycleId) return { items: [], total: 0 };
      const res = await apiRequest<Partial<ListResponse<unknown>>>(
        '/api/salaries/payments',
        { signal, query: { cycleId: selectedCycleId, ...apiParams } },
      );
      // The endpoint returns the joined shape ({ payment, payee }); the resilient
      // normaliser also tolerates legacy raw rows. We read `.total` for the
      // result count + pagination and keep `.items` as the grid rows.
      return {
        items: (res.items ?? []).map(normaliseSalaryRow),
        total: typeof res.total === 'number' ? res.total : 0,
      };
    },
  });

  const selectedCycle = cyclesQuery.data?.find((c) => c.id === selectedCycleId) ?? null;
  const rows = paymentsQuery.data?.items ?? [];
  const total = paymentsQuery.data?.total ?? 0;

  const openPayslip = async (paymentId: string) => {
    try {
      await openApiDocument(`/api/salaries/payments/${encodeURIComponent(paymentId)}/payslip-url`);
    } catch (err) {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not open payslip', { variant: 'error' });
    }
  };

  const columns = useMemo<ColumnDef<SalaryPaymentJoined, unknown>[]>(
    () => [
      {
        id: 'payee',
        header: 'Payee',
        accessorFn: (r) => r.payee.name,
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-medium truncate">{row.original.payee.name}</div>
          </div>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        accessorFn: (r) => r.payee.role,
        cell: ({ row }) => (
          <span className="capitalize text-[color:var(--color-text-muted)]">
            {row.original.payee.role}
          </span>
        ),
      },
      {
        id: 'gross',
        header: 'Gross',
        accessorFn: (r) => r.payment.grossPaise,
        cell: ({ row }) => formatINR(row.original.payment.grossPaise),
      },
      {
        id: 'deductions',
        header: 'Deductions',
        accessorFn: (r) => r.payment.deductionsPaise,
        cell: ({ row }) =>
          row.original.payment.deductionsPaise > 0
            ? formatINR(row.original.payment.deductionsPaise)
            : '—',
      },
      {
        id: 'net',
        header: 'Net',
        accessorFn: (r) => r.payment.netPaise,
        cell: ({ row }) => (
          <span className="font-medium">{formatINR(row.original.payment.netPaise)}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (r) => r.payment.status,
        cell: ({ row }) => <SalaryStatusBadge status={row.original.payment.status} />,
      },
      {
        id: 'paidAt',
        accessorFn: (r) => r.payment.paidAt ?? '',
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Paid on"
            binding={SALARY_PAYMENTS_SORT_COLUMNS.paidAt}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: ({ row }) =>
          row.original.payment.paidAt
            ? formatDate(row.original.payment.paidAt, { withTime: true })
            : '—',
      },
      {
        id: 'payslip',
        header: 'Payslip',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.payment.status === 'paid' ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void openPayslip(row.original.payment.id);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gold-700 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500 rounded"
            >
              <FileText size={14} aria-hidden="true" />
              View
            </button>
          ) : (
            <span className="text-xs text-[color:var(--color-text-muted)]">—</span>
          ),
      },
      ...(canManage
        ? ([
            {
              id: 'edit',
              header: '',
              enableSorting: false,
              size: 48,
              cell: ({ row }) => {
                const r = row.original;
                // Paid, on hold, or still ₹0 (set the amount first): nothing to mark.
                if (r.payment.status !== 'pending' || r.payment.netPaise <= 0) {
                  return <span aria-hidden="true" className="block" />;
                }
                return (
                  <div className="flex justify-end">
                    <EditIconButton
                      label="Mark paid"
                      title="Mark paid"
                      onClick={() => setMarkPaidPayment(r)}
                    />
                  </div>
                );
              },
            },
          ] satisfies ColumnDef<SalaryPaymentJoined, unknown>[])
        : []),
    ],
    // Rebuild columns when role gating or the active sort changes so the
    // sortable Paid header stays in sync with the visible rows.
    [canManage, values.sort, setSort],
  );

  return (
    <ListOrOutlet basePath="/finance/salaries">
    <ListShell
      title="Salaries"
      description="Monthly payouts for teachers and nalims."
      primaryAction={
        <Button icon={<Plus size={16} />} onClick={() => setNewCycleOpen(true)}>
          New cycle
        </Button>
      }
    >
      <div className="md:hidden">
        <CycleSelect
          cycles={cyclesQuery.data ?? []}
          selectedId={selectedCycleId}
          onSelect={setSelectedCycleId}
          ariaLabel="Select salary cycle"
        />
      </div>

      <div className="grid gap-5 md:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="hidden md:block min-w-0">
          <h2 className="px-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)] mb-2">
            Cycles
          </h2>
          {cyclesQuery.error ? (
            <ErrorState
              title="Could not load cycles"
              onRetry={() => cyclesQuery.refetch()}
            />
          ) : (
            <CycleSidebar
              cycles={cyclesQuery.data ?? []}
              selectedId={selectedCycleId}
              onSelect={setSelectedCycleId}
              isLoading={cyclesQuery.isLoading}
              dateLabel={(c) => `Payout ${formatDate(`${c.payoutDate}T00:00:00Z`)}`}
              emptyLabel="No salary cycles yet."
              renderLink={(c, { ariaLabel }) => (
                <Link
                  to="/finance/salaries/$cycleId"
                  params={{ cycleId: c.id }}
                  aria-label={ariaLabel}
                  className="absolute inset-0 rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500"
                />
              )}
            />
          )}
        </aside>

        <section className="min-w-0 space-y-3">
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold truncate">
              {selectedCycle
                ? cycleLabel(selectedCycle.year, selectedCycle.month)
                : 'Select a cycle'}
            </h2>
            {selectedCycle ? (
              <p className="text-xs text-[color:var(--color-text-muted)]">
                Payout {formatDate(`${selectedCycle.payoutDate}T00:00:00Z`)}
              </p>
            ) : null}
          </div>

          <FilterBar
            config={salaryPaymentsFilterConfig}
            filters={filters}
            resultCount={total}
          />

          {paymentsQuery.error ? (
            <ErrorState
              title="Could not load payments"
              description={
                paymentsQuery.error instanceof ApiError
                  ? paymentsQuery.error.message
                  : undefined
              }
              onRetry={() => paymentsQuery.refetch()}
            />
          ) : (
            <>
              <DataTable<SalaryPaymentJoined>
                data={rows}
                columns={columns}
                isLoading={paymentsQuery.isLoading}
                onRowClick={(r) =>
                  void navigate({
                    to: '/finance/salaries/payments/$paymentId',
                    params: { paymentId: r.payment.id },
                  })
                }
                getRowId={(r) => r.payment.id}
                emptyState={
                  <EmptyState
                    title={selectedCycleId ? 'No payments match these filters' : 'Pick a cycle'}
                    description={
                      selectedCycleId
                        ? 'Try clearing a filter to see more salary records.'
                        : 'Choose a cycle from the list to view payouts.'
                    }
                  />
                }
              />
              {selectedCycleId ? (
                <Pagination
                  page={values.page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onPageChange={setPage}
                />
              ) : null}
            </>
          )}
        </section>
      </div>

      <NewSalaryCycleDrawer
        open={newCycleOpen}
        onClose={() => setNewCycleOpen(false)}
        onCreated={async (cycleId, created) => {
          await queryClient.invalidateQueries({ queryKey: ['salaryCycles'] });
          setSelectedCycleId(cycleId);
          toast(`Cycle created and ${created} records seeded.`, { variant: 'success' });
        }}
      />

      <SalaryMarkPaidDrawer
        joined={markPaidPayment}
        onClose={() => setMarkPaidPayment(null)}
        onMarked={async () => {
          await queryClient.invalidateQueries({ queryKey: ['salaryPayments'] });
          toast('Salary marked as paid.', { variant: 'success' });
          setMarkPaidPayment(null);
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}

// ---------- New cycle drawer ----------

const currentYear = () => new Date().getFullYear();
const currentMonth = () => new Date().getMonth() + 1;
const todayIsoDate = () => new Date().toISOString().slice(0, 10);

type NewSalaryCycleValues = SalaryCycleInput;

interface NewSalaryCycleDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated: (cycleId: string, created: number) => Promise<void> | void;
}

function NewSalaryCycleDrawer({ open, onClose, onCreated }: NewSalaryCycleDrawerProps) {
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<NewSalaryCycleValues>({
    resolver: zodResolver(salaryCycleCreateSchema),
    defaultValues: {
      year: currentYear(),
      month: currentMonth(),
      payoutDate: todayIsoDate(),
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        year: currentYear(),
        month: currentMonth(),
        payoutDate: todayIsoDate(),
      });
    }
  }, [open, reset]);

  const onSubmit = async (values: NewSalaryCycleValues) => {
    try {
      const created = await apiRequest<{ id: string }>('/api/salaries/cycles', {
        method: 'POST',
        body: values,
      });
      let recordsCreated = 0;
      try {
        const gen = await apiRequest<{ ok: true; created: number }>(
          `/api/salaries/cycles/${encodeURIComponent(created.id)}/generate-records`,
          { method: 'POST' },
        );
        recordsCreated = gen.created;
      } catch (err) {
        if (err instanceof ApiError) toast(err.message, { variant: 'error' });
        else toast('Cycle created, but record generation failed.', { variant: 'error' });
      }
      await onCreated(created.id, recordsCreated);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'cycle_exists') {
          setError('month', { message: 'A cycle already exists for this month' });
        }
        toast(err.message, { variant: 'error' });
      } else {
        toast('Could not create cycle.', { variant: 'error' });
      }
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title="New salary cycle" size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="Year"
            type="number"
            min={2020}
            max={2100}
            {...register('year', { valueAsNumber: true })}
            error={errors.year?.message}
          />
          <SelectField
            label="Month"
            {...register('month', { valueAsNumber: true })}
            error={errors.month?.message}
            options={Array.from({ length: 12 }).map((_, i) => ({
              value: String(i + 1),
              label: new Date(2000, i, 1).toLocaleString('en-IN', { month: 'long' }),
            }))}
          />
        </div>
        <TextField
          label="Payout date"
          type="date"
          {...register('payoutDate')}
          error={errors.payoutDate?.message}
        />
        <TextAreaField
          label="Notes"
          optional
          {...register('notes')}
          error={errors.notes?.message}
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Create cycle
          </Button>
        </div>
      </form>
    </Drawer>
  );
}
